import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, LayoutGroup } from 'framer-motion';

const PHOTO_URL =
  'https://bardobggjtozmuyyadhf.supabase.co/storage/v1/object/public/asfdd//files_7358450-2026-02-17T22-51-29-101Z-Blue_and_White_Simple_Man_LinkedIn_Profile_Picture_20251118_075129_0000.png';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const sections = ['about', 'experience', 'education', 'skills', 'projects', 'contact'];

    let sectionOffsets: { id: string; top: number }[] = [];
    const cacheOffsets = () => {
      sectionOffsets = sections.map((id) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top + window.scrollY : 0 };
      });
    };
    cacheOffsets();

    window.addEventListener('resize', cacheOffsets, { passive: true });

    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        setScrolled(scrollY > 20);
        for (let i = sectionOffsets.length - 1; i >= 0; i--) {
          if (scrollY >= sectionOffsets[i].top - 100) {
            setActiveSection(sectionOffsets[i].id);
            break;
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', cacheOffsets);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const navLinks = [
    { href: '#about', label: 'About', anchor: true },
    { href: '#experience', label: 'Experience', anchor: true },
    { href: '#education', label: 'Education', anchor: true },
    { href: '#skills', label: 'Skills', anchor: true },
    { href: '/projects', label: 'Projects', anchor: false },
    { href: '/blog', label: 'Blog', anchor: false },
    { href: '/videos', label: 'Videos', anchor: false },
    { href: '/contact', label: 'Contact', anchor: false },
  ];

  const handleNavClick = (link: { href: string; anchor: boolean }) => {
    setIsOpen(false);
    if (!link.anchor) {
      navigate(link.href);
    } else if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'nav-blur shadow-sm border-b border-border/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-18 py-2 sm:py-3 gap-3">

          {/* Logo / Brand */}
          <button
            className="flex items-center gap-3 sm:gap-3.5 group focus:outline-none min-w-0"
            aria-label="Go to homepage"
            onClick={() => {
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                navigate('/');
              }
            }}
          >
            {/* Avatar with ring */}
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full ring-2 ring-accent/50 ring-offset-2 ring-offset-background overflow-hidden shadow-card group-hover:ring-accent transition-all duration-300">
                <img
                  src={PHOTO_URL}
                  alt="Dr. Yesh Jitesh"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <span aria-hidden="true" className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-gradient-to-br from-green-400 to-green-500 rounded-full border-2 border-background shadow-sm" />
            </div>

            {/* Name — visible on mobile too, compact */}
            <div className="text-left min-w-0">
              <p
                className="text-sm font-bold text-foreground leading-none group-hover:text-gradient transition-colors duration-300 truncate"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Dr. Yesh Jitesh
              </p>
              <p className="hidden sm:block text-[10px] text-muted-foreground leading-none mt-1 font-medium tracking-wide truncate">
                GP Assistant · HealthTech Founder
              </p>
            </div>
          </button>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            <LayoutGroup id="nav-pill">
              {navLinks.map((link) => {
                const sectionId = link.href.replace('#', '').replace('/', '');
                const isActive = link.anchor
                  ? activeSection === sectionId
                  : location.pathname === link.href;
                return (
                  <button
                    key={link.href}
                    onClick={() => handleNavClick(link)}
                    className={`relative px-4 py-2.5 text-sm font-medium rounded-xl transition-colors duration-300 ${
                      isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-pill"
                        className="absolute inset-0 rounded-xl bg-primary/8"
                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                      />
                    )}
                    <span className="relative z-10">{link.label}</span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-underline"
                        className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-4 h-0.5 gradient-premium rounded-full"
                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                      />
                    )}
                  </button>
                );
              })}
            </LayoutGroup>

            <button
              onClick={() => navigate('/contact')}
              className="ml-4 px-6 py-2.5 text-sm font-semibold gradient-premium text-primary-foreground rounded-xl hover:opacity-95 hover:-translate-y-px active:translate-y-0 transition-all duration-300 shadow-card hover:shadow-hero"
            >
              Get in Touch
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="border-t border-border bg-card/95 backdrop-blur-lg py-4 space-y-1 rounded-b-2xl shadow-elevated">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className="block w-full text-left px-5 py-3.5 min-h-[44px] text-sm font-medium text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors rounded-xl touch-manipulation"
              >
                {link.label}
              </button>
            ))}
            <div className="px-4 pt-3 pb-2">
              <button
                onClick={() => navigate('/contact')}
                className="w-full py-3.5 min-h-[44px] text-sm font-semibold gradient-premium text-primary-foreground rounded-xl hover:opacity-95 transition-opacity shadow-card"
              >
                Get in Touch
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
